class Resource_Set {
	constructor(data) {
		if (empty(data)) data = {}

		// if data is a framework_record object, this is a set represented by a CASE framework
		if (!empty(data.lsdoc_identifier)) {
			this.framework_record = data

			this.resource_set_id = this.framework_record.json.CFDocument.identifier
			this.resource_set_title = this.framework_record.json.CFDocument.title

			// don't worry about the creator and editors for now...
			this.creator_user_id = 0
			this.creator_email = ''
			this.editor_emails = []

			// look for created_at in document.extensions; if not there use lastChangeDateTime
			if (this.framework_record.json.CFDocument.extensions?.createdAt) {
				this.created_at = this.framework_record.json.CFDocument.extensions.createdAt
			} else {
				this.created_at = this.framework_record.json.CFDocument.lastChangeDateTime.replace(/(.*?)T(.*)+.*/, '$1 $2')
			}

			// get resources from CFItems
			this.resources = []
			this.resource_count = 0
			this.resources_loaded = false
			this.load_resources_from_cfitems()

		// else this is a resource set administered via the dedicated resource tables in the Satchel database
		} else {
			sdp(this, data, 'resource_set_id', 0)	// this should always be filled in
			sdp(this, data, 'resource_set_title', '')
			sdp(this, data, 'creator_user_id', 0)
			sdp(this, data, 'creator_email', '')	// not currently used
			sdp(this, data, 'editor_emails', [])
			sdp(this, data, 'created_at', '')

			sdp(this, data, 'resources_loaded', false)

			// note that the resource_count and resources, as well as some other vals (see copy_for_save), are not actually stored in the resource DB table
			sdp(this, data, 'resource_count', 0)
			this.resources = []
			if (!empty(data.resources)) {
				for (let r of data.resources) {
					this.resources.push(new Resource(r))
				}
			}
		}
	}

	load_resources_from_cfitems() {
		// if the framework_record is loaded, load the framework's resources, and alignments
		if (this.framework_record.framework_json_loaded) {
			this.resources = []
			this.resource_count = 0
			for (let cfitem of this.framework_record.json.CFItems) {
				if (U.is_resource_item(cfitem)) {
					this.resources.push(new Resource(cfitem, this.framework_record))
					++this.resource_count
				}
			}

			this.resources_loaded = true
		}
	}

	update_resources_from_cfitems() {
		// update the framework's resources alignments if anything has changed 
		if (this.framework_record.framework_json_loaded) {
			this.resource_count = 0
			for (let cfitem of this.framework_record.json.CFItems) {
				if (U.is_resource_item(cfitem)) {
					let resource_index = this.resources.findIndex(x=>x.cfitem?.identifier == cfitem.identifier)

					// if we didn't have a resource for this item, create one
					if (resource_index == -1) {
						this.resources.push(new Resource(cfitem, this.framework_record))
						console.warn('adding new resource')

					// else if the cfitem has been updated since we originally loaded the resource, re-initialize it
					} else if (this.resources[resource_index].cfitem_lastChangeDateTime != cfitem.lastChangeDateTime) {
						this.resources.splice(resource_index, 1, new Resource(cfitem, this.framework_record))
						console.warn('updating resource')
					}
					++this.resource_count
				}
			}

			// remove any resources that no longer exist in the framework
			for (let i = this.resources.length - 1; i >= 0; --i) {
				let resource = this.resources[i]
				if (!this.framework_record.json.CFItems.find(x=>x.identifier == resource.cfitem?.identifier)) {
					console.warn('splicing now-removed resource')
					this.resources.splice(i, 1)
				}
			}
		}
	}

	copy_for_save(flag) {
		let framework_record = this.framework_record
		delete this.framework_record

		let o = $.extend(true, {}, this)

		// remove things we don't want to save in the db for this object record
		delete o.creator_email
		delete o.resources_loaded
		delete o.resource_count
		delete o.resources	// note that resources must be saved separately
		delete o.created_at

		this.framework_record = framework_record

		return o
	}
}
window.Resource_Set = Resource_Set

class Resource {
	constructor(data, framework_record) {
		if (empty(data)) data = {}

		// do this to support constructing a new copy of a resource previously-created from a CASE item
		let cfitem
		if (!empty(data.cfitem)) {
			cfitem = data.cfitem
			framework_record = data.framework_record
		} else if (!empty(framework_record)) {
			cfitem = data
		}

		// if we receive a framework_record, this is a resource represented by a CASE item; data will be the cfitem
		if (!empty(framework_record)) {
			this.framework_record = framework_record
			this.cfitem = cfitem

			// a new resource is designated with resource_id 1, so if we receive an explicit resource_id of 1, use that here; we'll update the resource_id if the new resource is saved
			this.resource_id = (data.resource_id == 1) ? 1 : cfitem.identifier
			this.resource_set_id = framework_record.lsdoc_identifier

			// call update_from_cfitem to transfer data from the cfitem to the resource object
			this.update_from_cfitem(cfitem)

			// alignments from CFAssociations
			this.alignments = []
			let assocs = framework_record.cfo.associations_hash[this.resource_id]
			if (assocs) for (let assoc of assocs) {
				if (assoc.associationType == 'ext:isAlignedTo') {
					this.alignments.push(new Resource_Alignment(assoc))
				}
			}

		// else this is a resource administered via the dedicated resource tables in the Satchel database
		} else {
			sdp(this, data, 'resource_id', '0')	// this should always be filled in
			sdp(this, data, 'resource_set_id', '0')	// this should always be filled in

			// convert to numbers if possible
			if (!isNaN(this.resource_id*1)) this.resource_id = this.resource_id * 1
			if (!isNaN(this.resource_set_id*1)) this.resource_set_id = this.resource_set_id * 1

			sdp(this, data, 'external_resource_id', '')	// this should always be filled in
			sdp(this, data, 'creator_user_id', 0)
			sdp(this, data, 'created_at', '')

			sdp(this, data, 'resource_title', '')
			sdp(this, data, 'description', '')
			sdp(this, data, 'text', '')
			sdp(this, data, 'full_content', '')
			sdp(this, data, 'primary_aligned_competency', '')

			sdp(this, data, 'url', '')
			sdp(this, data, 'educationLevel', [])

			// code custom extensions, but only if there
			for (let field in data) {
				if (field.indexOf('ext:') == 0) {
					this[field] = data[field]
				}
			}

			this.alignments = []
			if (!empty(data.alignments)) {
				for (let a of data.alignments) {
					this.alignments.push(new Resource_Alignment(a))
				}
			}
		}

		// hack: if educationLevel starts with 'K', replace with 'KG'
		if (this.educationLevel[0] == 'K') this.educationLevel[0] = 'KG'

		// add things used in alignment tool
		sdp(this, data, 'search_terms', '')		// "temporary" keywords -- not stored as part of the item
		sdp(this, data, 'comp_strings', [])
		sdp(this, data, 'comp_string_vectors', [])
		sdp(this, data, 'candidates', [])
		sdp(this, data, 'candidates_showing', 0)
		sdp(this, data, 'candidate_params', '')
		sdp(this, data, 'alt_ed_level', [])
	}

	// NOTE: for some reason we seem to get more random random numbers if we include the 'xxx' in seedrandom
	random_icon() {
		let icons = [
			'alicorn', 'ant', 'bat', 'bee', 'bird', 'cat', 'cow', 'crab', 'crow', 'deer', 'dinosaur', 'dog', 'dolphin', 'dove', 'dragon', 'duck', 'elephant', 'fish-fins', 'fish', 'frog', 'hippo', 'horse', 'hydra', 'kiwi-bird', 'lobster', 'locust', 'monkey', 'mosquito', 'mouse-field', 'narwhal', 'octopus', 'otter', 'pegasus', 'pig', 'rabbit', 'raccoon', 'ram', 'sheep', 'shrimp', 'snake', 'spider', 'squid', 'squirrel', 't-rex', 'turtle', 'unicorn', 'whale', 'worm',
		]

		if (!this.external_resource_id) return 'cube'
		let rng = new Math.seedrandom(this.external_resource_id + 'xxx')
		return icons[Math.floor(rng()*icons.length)]
	}

	random_icon_rotation() {
		if (!this.external_resource_id) return 0
		let rng = new Math.seedrandom(this.external_resource_id + 'xxx')
		return Math.round(rng()*360)
	}

	random_color() {
		if (!this.external_resource_id) return '#000'
		let rng = new Math.seedrandom(this.external_resource_id + this.resource_id + 'xxx')
		return `hsl(${Math.round(rng()*360)}, 100%, 30%)`
	}

	// transfer data from the cfitem to the resource object
	update_from_cfitem(cfitem) {
		let extensions = cfitem.extensions || {}

		// if externalIdentifier not provided in extensions, use the cfitem identifier (unless resource_id is 1)
		this.external_resource_id = extensions.externalIdentifier || ((this.resource_id == 1) ? '' : cfitem.identifier)
		this.resource_title = cfitem.fullStatement
		this.description = extensions.description || ''
		this.text = extensions.keywords || ''					// needs to be added to spec doc
		this.full_content = extensions.fullContent || ''		// needs to be added to spec doc
		this.url = extensions.resourceURL || ''
		this.educationLevel = cfitem.educationLevel ? object_copy(cfitem.educationLevel) : []

		// look for created_at in item.extensions; if not there use lastChangeDateTime
		if (cfitem.extensions?.createdAt) {
			this.created_at = cfitem.extensions.createdAt
		} else if (!empty(cfitem.lastChangeDateTime)) {
			this.created_at = cfitem.lastChangeDateTime.replace(/(.*?)T(.*)+.*/, '$1 $2')
		} else {
			this.created_at = ''
		}

		if (cfitem.extensions?.primaryAlignedCompetency) {
			this.primary_aligned_competency = cfitem.extensions.primaryAlignedCompetency
		}
		
		// any additional extensions get stashed in customExtensions -- but only if we have any
		for (let field in extensions) {
			if (field.indexOf('ext:') == 0) {
				this[field] = extensions[field]
			}
		}

		// we won't worry about this for now
		this.creator_user_id = 0

		// save the cfitem's lastChangeDateTime, so we can check for updates later
		this.cfitem_lastChangeDateTime = cfitem.lastChangeDateTime
	}

	// this is the reverse of the previous fn: transfer data from the resource object to the provided cfitem
	update_cfitem(cfitem) {
		// note that in this fn, we do *not* clear values that aren't set in the resource, so we don't blow away values that already exist for the resouce
		if (empty(cfitem.extensions)) cfitem.extensions = {}
		if (this.external_resource_id) cfitem.extensions.externalIdentifier = this.external_resource_id
		if (this.resource_title) cfitem.fullStatement = this.resource_title
		if (this.description) cfitem.extensions.description = this.description
		if (this.text) cfitem.extensions.keywords = this.text
		if (this.full_content) cfitem.extensions.fullContent = this.full_content
		if (this.url) cfitem.extensions.resourceURL = this.url
		if (this.educationLevel && this.educationLevel.length > 0) cfitem.educationLevel = object_copy(this.educationLevel)
		if (this.created_at) cfitem.extensions.createdAt = this.created_at
		if (this.primary_aligned_competency) cfitem.extensions.primaryAlignedCompetency = this.primary_aligned_competency

		// deal with custom extensions
		for (let field in this) {
			if (field.indexOf('ext:') == 0) {
				cfitem.extensions[field] = this[field]
			}
		}
	}

	static resource_matches_case_item(resource, cfitem) {
		if (resource.external_resource_id == cfitem.extensions?.externalIdentifier) return true
		if (resource.external_resource_id == cfitem.identifier) return true
		return false
	}

	matches_case_item(cfitem) {
		return Resource.resource_matches_case_item(this, cfitem)
	}

	// transfer data from this resource object to a cfitem
	cfitem_for_save() {
		// start with the original cfitem for the item
		let cfitem = this.framework_record.json.CFItems.find(x=>x.identifier == this.resource_id)
		if (empty(cfitem)) {
			// console.warn(`cfitem_for_save: couldn’t find resource '${this.resource_id}' in CFItems (not necessarily an error…)`)
			cfitem = {}
		}

		cfitem = object_copy(cfitem)
		if (empty(cfitem.extensions)) cfitem.extensions = {}

		// only change the fields we map onto resource data above
		cfitem.extensions.externalIdentifier = this.external_resource_id
		cfitem.fullStatement = this.resource_title
		cfitem.extensions.description = this.description
		cfitem.extensions.keywords = this.text
		cfitem.extensions.fullContent = this.full_content
		cfitem.extensions.resourceURL = this.url
		cfitem.educationLevel = this.educationLevel ? object_copy(this.educationLevel) : []
		cfitem.extensions.createdAt = this.created_at
		cfitem.extensions.primaryAlignedCompetency = this.primary_aligned_competency

		// deal with custom extensions
		for (let field in this) {
			if (field.indexOf('ext:') == 0) {
				cfitem.extensions[field] = this[field]
			}
		}
		
		// note that we *don't* set the identifier for the cfitem

		return cfitem
	}

	copy_for_save(flag) {
		// remove the framework_record from the resource object before we extend the object, then put it back
		let framework_record = this.framework_record
		delete this.framework_record
		let o = $.extend(true, {}, this)
		this.framework_record = framework_record

		// remove things we don't want to save in the db for this object record
		delete o.search_terms
		delete o.created_at
		delete o.alignments	// note that alignments must be saved separately
		delete o.comp_strings
		delete o.comp_string_vectors
		delete o.alignments_for_matching
		delete o.candidates
		delete o.candidate_params
		delete o.alt_ed_level

		delete o.cfitem
		delete o.cfitem_lastChangeDateTime

		return o
	}

}
window.Resource = Resource

class Resource_Alignment {
	constructor(data) {
		if (empty(data)) data = {}

		// if data has an identifier, this is a resource represented by a CASE association; data will be the cfassociation
		if (!empty(data.identifier)) {
			this.cfassociation = data
			
			this.resource_alignment_id = data.identifier
			this.resource_id = data.originNodeURI.identifier
			if (!empty(data.extensions?.frameworkIdentifier)) {
				this.framework_identifier = data.extensions.frameworkIdentifier
			} else if (data.destinationNodeURI.title.search(/\(:(.*?)\:\)$/) > -1) {
				this.framework_identifier = RegExp.$1
			} else {
				this.framework_identifier = ''
			}
			this.item_identifier = data.destinationNodeURI.identifier

			// look for created_at in assoc.extensions; if not there use lastChangeDateTime
			if (data.extensions?.createdAt) {
				this.created_at = data.extensions.createdAt
			} else {
				this.created_at = data.lastChangeDateTime.replace(/(.*?)T(.*)+.*/, '$1 $2')
			}
			
			// we won't worry about this for now
			this.creator_user_id = 0

		// else this is an alignment administered via the dedicated resource tables in the Satchel database
		} else {
			sdp(this, data, 'resource_alignment_id', '0')
			sdp(this, data, 'resource_id', '0')

			// convert to numbers if possible
			if (!isNaN(this.resource_alignment_id*1)) this.resource_alignment_id = this.resource_alignment_id * 1
			if (!isNaN(this.resource_set_id*1)) this.resource_set_id = this.resource_set_id * 1

			sdp(this, data, 'framework_identifier', '')
			sdp(this, data, 'item_identifier', '')
			sdp(this, data, 'creator_user_id', 0)
			sdp(this, data, 'created_at', '')
		}
	}

	copy_for_save(flag) {
		let o = $.extend(true, {}, this)

		// remove things we don't want to save in the db for this object record
		delete o.created_at
		delete o.cfitem	// this might be added when creating new alignments
		delete o.cfitem_vector	// this might be added when creating new alignments
		delete o.sme_assocs	// this might be added when creating new alignments

		delete o.cfassociation

		return o
	}
}
window.Resource_Alignment = Resource_Alignment

class Resource_Alignment_Candidate {
	constructor(data) {
		if (empty(data)) data = {}

		sdp(this, data, 'framework_identifier', '')
		sdp(this, data, 'cfitem', null)
		sdp(this, data, 'comps', {
			education_level: -1,
			item: 0,
			assocs: 0,

			// TODO: delete these when we move to new version
			parent: 0,
			sme_assocs: 0,
			ai_assocs: 0,
		})
		sdp(this, data, 'is_alignable', false)
		sdp(this, data, 'is_ancestor', false)
		sdp(this, data, 'currently_aligned', false)
		sdp(this, data, 'simscore', -1)
		sdp(this, data, 'simscore_pct', -1)
		sdp(this, data, 'simscore_pct_norm', -1)
	}
}
window.Resource_Alignment_Candidate = Resource_Alignment_Candidate
